import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewEncapsulation,
    input,
    inject,
    computed,
    signal,
    viewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { PlatformService } from '@seven1/angular/ssr';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { ITrustpilotBlock, TrustpilotData, TRUSTPILOT_SERVER_DATA, TRUSTPILOT_TRANSLATIONS } from './trustpilot.model';

@Component({
    selector: 'div[s1-trustpilot]',
    templateUrl: './trustpilot.component.html',
    styleUrl: './trustpilot.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'flex justify-center items-center',
        '[class]': 'classes()',
        '[attr.id]': 'block().componentId',
    },
})
export class TrustpilotComponent {
    private _platformService = inject(PlatformService);
    private _tailwind = inject(TailwindService);
    private _http = inject(HttpClient);
    private _serverData = inject(TRUSTPILOT_SERVER_DATA);

    block = input.required<ITrustpilotBlock>();
    track = viewChild<ElementRef<HTMLElement>>('track');

    private _lang = computed(() => this.block().language || 'de');

    t = computed(() => TRUSTPILOT_TRANSLATIONS[this._lang()]);

    reviews = computed(() => {
        const lang = this.block().reviewLanguage || this._lang();
        return this.data()?.reviews?.[lang] ?? this.data()?.reviews?.['de'] ?? [];
    });

    data = this._platformService.isBrowser
        ? toSignal(this._http.get<TrustpilotData>('/data/trustpilotData.json'), {
            initialValue: this._serverData,
        })
        : signal<TrustpilotData>(this._serverData);

    classes = computed(() => {
        const block = this.block();
        return [
            ...this._tailwind.getSpacingClasses(block),
            ...this._tailwind.getDisplayClasses(block),
        ];
    });

    // Official TrustPilot TrustScore → Stars mapping:
    // 1.0-1.2 → 1, 1.3-1.7 → 1.5, 1.8-2.2 → 2, 2.3-2.7 → 2.5,
    // 2.8-3.2 → 3, 3.3-3.7 → 3.5, 3.8-4.2 → 4, 4.3-4.7 → 4.5, 4.8-5.0 → 5
    starClass = computed(() => {
        const score = this.data()?.score?.trustScore ?? 0;
        if (score >= 4.8) return 'tp-stars--5';
        if (score >= 4.3) return 'tp-stars--4--half';
        if (score >= 3.8) return 'tp-stars--4';
        if (score >= 3.3) return 'tp-stars--3--half';
        if (score >= 2.8) return 'tp-stars--3';
        if (score >= 2.3) return 'tp-stars--2--half';
        if (score >= 1.8) return 'tp-stars--2';
        if (score >= 1.3) return 'tp-stars--1--half';
        if (score >= 1.0) return 'tp-stars--1';
        return 'tp-stars--0';
    });

    // Official TrustPilot Rating labels:
    // Bad: 1.0-1.7, Poor: 1.8-2.7, Average: 2.8-3.7, Great: 3.8-4.2, Excellent: 4.3-5.0
    ratingLabel = computed(() => {
        const score = this.data()?.score?.trustScore ?? 0;
        const t = this.t();
        if (score >= 4.3) return t.excellent;
        if (score >= 3.8) return t.great;
        if (score >= 2.8) return t.average;
        if (score >= 1.8) return t.poor;
        return t.bad;
    });

    scrollCarousel(direction: 'left' | 'right'): void {
        const el = this.track()?.nativeElement;
        if (!el) return;
        const cardWidth = 220 + 16; // card width + gap
        el.scrollBy({ left: direction === 'right' ? cardWidth : -cardWidth, behavior: 'smooth' });
    }

    relativeDate(isoDate: string): string {
        const now = new Date();
        const date = new Date(isoDate);
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const lang = this._lang();

        const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
        if (diffDays < 1) return rtf.format(0, 'day');
        if (diffDays < 7) return rtf.format(-diffDays, 'day');
        if (diffDays < 30) return rtf.format(-Math.floor(diffDays / 7), 'week');
        if (diffDays < 365) return rtf.format(-Math.floor(diffDays / 30), 'month');
        return rtf.format(-Math.floor(diffDays / 365), 'year');
    }
}
